
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { User, Save, AlertCircle, Zap, Check } from 'lucide-react';
import WritingPrompts from '@/components/WritingPrompts';

interface BiographyEditorProps {
  biography: string;
  setBiography: (value: string) => void;
  saving: boolean;
  onSave: () => void;
  wordCount: number;
  maxCharacters: number;
}

const BiographyEditor: React.FC<BiographyEditorProps> = ({
  biography,
  setBiography,
  saving,
  onSave,
  wordCount,
  maxCharacters
}) => {
  const [justSaved, setJustSaved] = useState(false);

  const handleBiographyChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    console.log('🔤 [BIOGRAPHY EDITOR] Text changed:', {
      newLength: newValue.length,
      newValue: newValue.substring(0, 100) + (newValue.length > 100 ? '...' : ''),
      timestamp: new Date().toISOString()
    });
    setBiography(newValue); // Always update, let parent handle limits
    if (justSaved) setJustSaved(false); // Reset saved state if user starts typing
  };

  const handleSave = async () => {
    console.log('💾 [BIOGRAPHY EDITOR] Save button clicked:', {
      biographyLength: biography.length,
      biographyPreview: biography.substring(0, 100) + (biography.length > 100 ? '...' : ''),
      timestamp: new Date().toISOString()
    });
    await onSave();
    setJustSaved(true);
    setTimeout(() => setJustSaved(false), 3000); // Show success for 3 seconds
  };

  // Reset justSaved when saving starts
  useEffect(() => {
    if (saving) setJustSaved(false);
  }, [saving]);

  const getCharacterCountColor = () => {
    const percentage = (biography.length / maxCharacters) * 100;
    if (percentage >= 90) return 'text-red-600';
    if (percentage >= 75) return 'text-orange-600';
    return 'text-gray-500';
  };

  return (
    <Card className="meditation-card border-border/50 bg-card/80 backdrop-blur-sm">
      <CardHeader className="pb-4">
        <CardTitle className="flex items-center text-foreground font-medium">
          <User className="h-5 w-5 mr-3 text-primary opacity-80" />
          Personal Biography
        </CardTitle>
        <p className="text-sm text-muted-foreground font-light">
          Share your story to unlock deeply personalized sessions. Include names, hobbies, goals, and preferences - the more you share, the more meaningful your experience becomes.
        </p>
        <p className="text-xs text-muted-foreground/70 font-light mt-1">
          🌟 Gift a team or community? Include up to 15 people with their roles - each will be honored by name.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <textarea
            value={biography || ''}
            onChange={handleBiographyChange}
            placeholder="Example: I'm Sarah, a teacher from Portland who loves hiking with my dog Max. I enjoy yoga, reading mystery novels, and spending time with my partner Alex. I'm working on managing work stress and improving my sleep routine. I prefer gentle, encouraging guidance and natural sounds like rain or ocean waves..."
            className="w-full h-[200px] p-4 border border-border rounded-lg text-base bg-background resize-vertical"
            style={{ 
              pointerEvents: 'auto',
              position: 'relative',
              zIndex: 10,
              minHeight: '120px',
              maxHeight: '400px'
            }}
            spellCheck={true}
            autoComplete="off"
            autoCorrect="on"
            data-testid="biography-textarea"
          />

          {/* Writing assistance */}
          <WritingPrompts maxCharacters={maxCharacters} />
          
          <div className="flex justify-between items-center mt-2 text-sm">
            <span className="text-muted-foreground font-light">{wordCount} words</span>
            <span className={getCharacterCountColor()}>
              {biography.length}/{maxCharacters} characters
            </span>
          </div>
          
          {biography.length > maxCharacters * 0.8 && (
            <div className="mt-2 p-2 bg-accent/10 rounded-lg border border-accent/20">
              <p className="text-xs text-accent font-light">
                💡 Our AI will focus on the most important details for personalization.
              </p>
            </div>
          )}
        </div>

        <div className="space-y-2">
          <div className="flex items-center gap-2 p-3 bg-primary/5 rounded-lg border border-primary/20">
            <Zap className="h-4 w-4 text-primary flex-shrink-0" />
            <p className="text-xs text-primary font-light">
              Limit: {maxCharacters} characters for optimal performance
            </p>
          </div>
          
          <Button
            onClick={handleSave}
            disabled={saving || biography.length > maxCharacters}
            className={`w-full h-10 rounded-lg font-medium disabled:opacity-50 transition-all duration-300 ${
              justSaved 
                ? 'bg-green-600 hover:bg-green-700 text-white' 
                : 'bg-primary hover:bg-primary/90 text-primary-foreground'
            }`}
          >
            {justSaved ? (
              <>
                <Check className="h-4 w-4 mr-2" />
                Saved!
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                {saving ? 'Saving...' : 'Save Biography'}
              </>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default BiographyEditor;
